import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Upload, CheckCircle2, AlertTriangle, FileText, X, Loader2, Image as ImageIcon,
  Receipt, Layers, Edit3, Trash2, ChevronDown, ChevronUp, Eye, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiService } from '@/lib/api';

const COST_TYPES = [
  { id: 'paid', label: 'Paid', color: 'green', Icon: CheckCircle2 },
  { id: 'partial', label: 'Partial', color: 'blue', Icon: Layers },
  { id: 'invoice', label: 'Invoice', color: 'amber', Icon: FileText },
  { id: 'estimate', label: 'Estimate', color: 'slate', Icon: Edit3 },
];

const CONSTRUCTION_PHASES = [
  { number: 1, name: 'Planning and Permitting' },
  { number: 2, name: 'Site Preparation and Foundation' },
  { number: 3, name: 'Framing' },
  { number: 4, name: 'Exterior and Systems (Rough-in)' },
  { number: 5, name: 'Insulation and Drywall' },
  { number: 6, name: 'Interior Finishes' },
  { number: 7, name: 'Fixtures and Exterior Finishing' },
  { number: 8, name: 'Final Walkthrough and Handover' },
];

const MAX_BATCH = 50;
const PARALLEL_CHUNK = 4;

// File status pills
const StatusPill = ({ status }) => {
  const map = {
    queued: { label: 'Queued', cls: 'bg-slate-100 text-slate-600' },
    extracting: { label: 'Extracting', cls: 'bg-blue-100 text-blue-700 animate-pulse' },
    ready: { label: 'Ready', cls: 'bg-emerald-100 text-emerald-700' },
    review: { label: 'Needs review', cls: 'bg-amber-100 text-amber-700' },
    duplicate: { label: 'Duplicate', cls: 'bg-blue-100 text-blue-600' },
    error: { label: 'Error', cls: 'bg-red-100 text-red-700' },
    filed: { label: 'Filed', cls: 'bg-emerald-200 text-emerald-800' },
    skipped: { label: 'Skipped', cls: 'bg-slate-100 text-slate-500' },
  };
  const m = map[status] || map.queued;
  return <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${m.cls}`}>{m.label}</span>;
};

// Simple SHA-256 hash for in-batch dedup
async function sha256(file) {
  try {
    const buf = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', buf);
    const arr = Array.from(new Uint8Array(hashBuffer));
    return arr.map((b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return null;
  }
}

// Lazy-loaded PDF.js — only imported the first time a PDF arrives.
// The worker file is served as a static asset from /public/pdf.worker.mjs
// (copied during install). This avoids Vite-specific `?url` import syntax
// which breaks under CRA/webpack5.
let _pdfjsPromise = null;
const loadPdfJs = async () => {
  if (!_pdfjsPromise) {
    _pdfjsPromise = (async () => {
      const pdfjs = await import('pdfjs-dist/build/pdf.mjs');
      pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.mjs';
      return pdfjs;
    })().catch((err) => {
      _pdfjsPromise = null;
      throw err;
    });
  }
  return _pdfjsPromise;
};

/**
 * Render the first page of a PDF File into a data URL thumbnail.
 * Returns null on failure.
 */
const renderPdfThumbnail = async (file, maxDim = 600) => {
  try {
    const pdfjs = await loadPdfJs();
    const buf = await file.arrayBuffer();
    const pdf = await pdfjs.getDocument({ data: buf, isEvalSupported: false, useSystemFonts: true }).promise;
    const page = await pdf.getPage(1);
    const viewport = page.getViewport({ scale: 1 });
    const scale = maxDim / Math.max(viewport.width, viewport.height);
    const scaled = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    canvas.width = scaled.width;
    canvas.height = scaled.height;
    const ctx = canvas.getContext('2d');
    await page.render({ canvasContext: ctx, viewport: scaled }).promise;
    return canvas.toDataURL('image/jpeg', 0.85);
  } catch (e) {
    console.warn('PDF thumbnail render failed', e);
    return null;
  }
};

const BulkRow = ({ row, projects, onUpdate, onFile, onDiscard, onPreview }) => {
  const [phases, setPhases] = useState([]);
  const [expanded, setExpanded] = useState(row.status === 'review' || row.status === 'error');
  const [filing, setFiling] = useState(false);

  useEffect(() => {
    if (row.form?.project_id) {
      apiService.getPhases(row.form.project_id).then((res) => setPhases(res.data || [])).catch(() => setPhases([]));
    }
  }, [row.form?.project_id]);

  const handleSave = async () => {
    const f = row.form;
    if (!f.project_id) { toast.error('Pick a project'); return; }
    if (f.phase_mode === 'existing' && !f.phase_id) { toast.error('Pick a phase or create a new one'); return; }
    if (!f.amount || f.amount <= 0) { toast.error('Amount required'); return; }
    setFiling(true);
    try {
      await onFile(row);
    } finally {
      setFiling(false);
    }
  };

  const summary = row.extracted ? `${row.extracted.vendor || 'Unknown'} · $${(row.extracted.amount || 0).toFixed(2)}${row.extracted.date ? ' · ' + row.extracted.date : ''}` : 'No data extracted';

  return (
    <div className={`border rounded-xl overflow-hidden transition-colors ${
      row.status === 'filed' ? 'bg-emerald-50 border-emerald-200' :
      row.status === 'skipped' ? 'bg-slate-50 border-slate-200 opacity-70' :
      row.status === 'error' ? 'bg-red-50 border-red-200' :
      row.status === 'duplicate' ? 'bg-blue-50 border-blue-200' :
      'bg-white border-slate-200'
    }`}
    data-testid={`bulk-row-${row.id}`}
    >
      {/* Compact header */}
      <div className="px-3 py-2.5 flex items-center gap-3">
        {/* Thumbnail — bigger and clickable to open preview */}
        <button
          type="button"
          onClick={() => onPreview && onPreview(row)}
          className="w-16 h-16 rounded-md bg-slate-100 overflow-hidden flex-shrink-0 flex items-center justify-center relative group hover:ring-2 hover:ring-amber-400 transition-all"
          title="Click to preview"
          data-testid={`bulk-row-thumb-${row.id}`}
        >
          {row.previewUrl ? (
            <>
              <img src={row.previewUrl} alt={row.file?.name || 'preview'} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <Eye className="w-4 h-4 text-white" />
              </div>
            </>
          ) : row.thumbnailLoading ? (
            <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />
          ) : (
            <div className="flex flex-col items-center text-slate-400">
              <FileText className="w-5 h-5" />
              <span className="text-[8px] mt-0.5 uppercase font-bold">
                {row.file?.name?.split('.').pop()?.slice(0, 4) || 'FILE'}
              </span>
            </div>
          )}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5">
            <p className="font-semibold text-slate-900 text-xs truncate">{row.file?.name || 'Untitled'}</p>
            <StatusPill status={row.status} />
          </div>
          <p className="text-[11px] text-slate-500 truncate">{summary}</p>
          {row.status === 'duplicate' && row.duplicate && (
            <p className="text-[10px] text-blue-700 mt-0.5 truncate">
              Already filed as <span className="font-bold">{row.duplicate.phase_name || 'existing receipt'}</span> in {row.duplicate.project_name || 'this project'}
            </p>
          )}
          {row.status === 'error' && (
            <p className="text-[10px] text-red-700 mt-0.5">{row.errorMessage || 'AI couldn\u2019t read this — fill manually'}</p>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {(row.status === 'review' || row.status === 'ready' || row.status === 'duplicate' || row.status === 'error') && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="w-7 h-7 rounded-md hover:bg-slate-100 flex items-center justify-center"
              title={expanded ? 'Collapse' : 'Expand'}
              data-testid={`bulk-row-toggle-${row.id}`}
            >
              {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
          )}
          {row.status !== 'filed' && row.status !== 'skipped' && (
            <button
              onClick={() => onDiscard(row)}
              className="w-7 h-7 rounded-md hover:bg-red-50 flex items-center justify-center"
              title="Discard this file"
              data-testid={`bulk-row-discard-${row.id}`}
            >
              <Trash2 className="w-3.5 h-3.5 text-red-500" />
            </button>
          )}
        </div>
      </div>

      {/* Expanded edit form */}
      {expanded && row.status !== 'filed' && row.status !== 'skipped' && (
        <div className="px-3 pb-3 pt-1 border-t border-slate-100 space-y-2 bg-slate-50">
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-[10px]">Project</Label>
              <Select
                value={row.form?.project_id || ''}
                onValueChange={(v) => onUpdate(row.id, { project_id: v, phase_id: null })}
              >
                <SelectTrigger className="h-8 text-xs" data-testid={`bulk-row-project-${row.id}`}>
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map((p) => (
                    <SelectItem key={p.id} value={p.id} className="text-xs">{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px]">Cost Type</Label>
              <Select
                value={row.form?.cost_type || 'paid'}
                onValueChange={(v) => onUpdate(row.id, { cost_type: v })}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COST_TYPES.map((c) => (
                    <SelectItem key={c.id} value={c.id} className="text-xs">{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-[10px]">Phase</Label>
              <Select
                value={
                  row.form?.phase_mode === 'new'
                    ? '__new__'
                    : row.form?.phase_id || ''
                }
                onValueChange={(v) => {
                  if (v === '__new__') {
                    onUpdate(row.id, { phase_mode: 'new', phase_id: null });
                  } else {
                    onUpdate(row.id, { phase_mode: 'existing', phase_id: v });
                  }
                }}
              >
                <SelectTrigger className="h-8 text-xs" data-testid={`bulk-row-phase-${row.id}`}>
                  <SelectValue placeholder="Select phase" />
                </SelectTrigger>
                <SelectContent>
                  {phases.map((p) => (
                    <SelectItem key={p.id} value={p.id} className="text-xs">{p.name}</SelectItem>
                  ))}
                  <SelectItem value="__new__" className="text-xs font-bold text-blue-600">+ Create new phase</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px]">Amount</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={row.form?.amount || ''}
                onChange={(e) => onUpdate(row.id, { amount: parseFloat(e.target.value) || 0 })}
                className="h-8 text-xs"
                data-testid={`bulk-row-amount-${row.id}`}
              />
            </div>
          </div>
          {row.form?.phase_mode === 'new' && (
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-[10px]">New Phase Name</Label>
                <Input
                  value={row.form?.new_phase_name || ''}
                  onChange={(e) => onUpdate(row.id, { new_phase_name: e.target.value })}
                  placeholder="e.g., Lumber"
                  className="h-8 text-xs"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px]">Construction Phase</Label>
                <Select
                  value={String(row.form?.new_phase_construction_number || 1)}
                  onValueChange={(v) => onUpdate(row.id, { new_phase_construction_number: parseInt(v) })}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CONSTRUCTION_PHASES.map((cp) => (
                      <SelectItem key={cp.number} value={String(cp.number)} className="text-xs">{cp.number}. {cp.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-[10px]">Vendor</Label>
              <Input
                value={row.form?.vendor || ''}
                onChange={(e) => onUpdate(row.id, { vendor: e.target.value })}
                className="h-8 text-xs"
                data-testid={`bulk-row-vendor-${row.id}`}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px]">Date</Label>
              <Input
                type="date"
                value={row.form?.receipt_date || ''}
                onChange={(e) => onUpdate(row.id, { receipt_date: e.target.value })}
                className="h-8 text-xs"
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-1">
            {row.status === 'duplicate' && (
              <span className="text-[10px] text-blue-700 mr-auto">
                Click "File anyway" to override duplicate flag
              </span>
            )}
            <Button
              size="sm"
              onClick={handleSave}
              disabled={filing}
              className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid={`bulk-row-save-${row.id}`}
            >
              {filing ? <Loader2 className="w-3 h-3 animate-spin" /> : (row.status === 'duplicate' ? 'File anyway' : 'File this')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// =================== MAIN DIALOG ===================
export const BulkQuickCaptureDialog = ({ open, onOpenChange, onComplete }) => {
  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [seenHashes, setSeenHashes] = useState(new Set()); // for in-batch dedupe
  const [previewRow, setPreviewRow] = useState(null);

  const userId = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user'))?.id; } catch { return null; }
  }, []);

  // Reset on open
  useEffect(() => {
    if (open) {
      setRows([]);
      setSeenHashes(new Set());
      apiService.getProjects(userId)
        .then((res) => setProjects((res.data || []).filter((p) => p.status !== 'completed')))
        .catch(() => setProjects([]));
    }
  }, [open, userId]);

  const updateRow = useCallback((id, patch) => {
    setRows((prev) => prev.map((r) => r.id === id ? { ...r, ...patch, form: { ...r.form, ...(patch.form || {}), ...patch } } : r));
  }, []);

  const updateRowForm = useCallback((id, formPatch) => {
    setRows((prev) => prev.map((r) => r.id === id ? { ...r, form: { ...r.form, ...formPatch } } : r));
  }, []);

  const handleFiles = async (fileList) => {
    if (!userId) { toast.error('Please log in first'); return; }
    const files = Array.from(fileList || []);
    if (!files.length) return;

    const remaining = MAX_BATCH - rows.length;
    if (remaining <= 0) {
      toast.error(`Batch limit reached (${MAX_BATCH} files max)`);
      return;
    }
    const accept = files.slice(0, remaining);
    if (files.length > remaining) {
      toast.warning(`Only the first ${remaining} files were added (batch limit ${MAX_BATCH})`);
    }

    // Build initial rows with previews
    const newRows = await Promise.all(accept.map(async (file, idx) => {
      const hash = await sha256(file);
      const isImage = file.type?.startsWith('image/');
      const isPdf = file.type === 'application/pdf' || file.name?.toLowerCase().endsWith('.pdf');
      let previewUrl = null;
      let fullUrl = null;
      let thumbnailLoading = false;
      if (isImage) {
        previewUrl = URL.createObjectURL(file);
        fullUrl = previewUrl; // image preview = full preview
      } else if (isPdf) {
        // Full preview = blob URL pointing at the PDF (rendered in <iframe>)
        fullUrl = URL.createObjectURL(file);
        thumbnailLoading = true; // we will async-render the page-1 thumbnail
      }
      const id = `r-${Date.now()}-${idx}-${Math.random().toString(36).slice(2, 6)}`;
      return {
        id,
        file,
        hash,
        previewUrl,
        fullUrl,
        isPdf,
        thumbnailLoading,
        status: 'queued',
        extracted: null,
        duplicate: null,
        errorMessage: null,
        file_id: null,
        form: null,
      };
    }));

    // Filter in-batch duplicates: same hash within this drop is auto-skipped
    const filtered = [];
    const localSeen = new Set(seenHashes);
    for (const r of newRows) {
      if (r.hash && localSeen.has(r.hash)) {
        r.status = 'duplicate';
        r.duplicate = { same_batch: true, phase_name: 'identical file in this batch', project_name: '' };
      } else if (r.hash) {
        localSeen.add(r.hash);
      }
      filtered.push(r);
    }
    setSeenHashes(localSeen);
    setRows((prev) => [...prev, ...filtered]);

    // Asynchronously generate PDF page-1 thumbnails (doesn't block AI extraction)
    filtered.forEach(async (r) => {
      if (r.isPdf) {
        const dataUrl = await renderPdfThumbnail(r.file, 600);
        setRows((prev) => prev.map((x) => x.id === r.id ? { ...x, previewUrl: dataUrl, thumbnailLoading: false } : x));
      }
    });

    // Process in parallel chunks (only those marked queued)
    await processQueue(filtered.filter((r) => r.status === 'queued'));
  };

  const processQueue = async (queue) => {
    for (let i = 0; i < queue.length; i += PARALLEL_CHUNK) {
      const chunk = queue.slice(i, i + PARALLEL_CHUNK);
      // Mark extracting
      setRows((prev) => prev.map((r) => chunk.find((c) => c.id === r.id) ? { ...r, status: 'extracting' } : r));
      // Run in parallel
      await Promise.all(chunk.map((r) => extractOne(r)));
    }
  };

  const extractOne = async (row) => {
    try {
      const res = await apiService.quickCaptureExtract(userId, row.file, null);
      const data = res.data;
      const projectId = data.project_guess?.project_id || null;
      const phaseId = data.phase_suggestion?.match === 'existing' ? data.phase_suggestion.phase_id : null;
      const isNewPhase = data.phase_suggestion?.match === 'new';

      const form = {
        project_id: projectId,
        phase_mode: isNewPhase ? 'new' : 'existing',
        phase_id: phaseId,
        new_phase_name: data.phase_suggestion?.phase_name || data.extracted?.vendor || '',
        new_phase_construction_number: data.phase_suggestion?.construction_phase_number || 1,
        new_phase_type: data.phase_suggestion?.suggested_phase_type || data.extracted?.suggested_category || 'material',
        cost_type: data.extracted?.suggested_cost_type || 'paid',
        amount: data.extracted?.amount ?? 0,
        paid_amount: data.extracted?.amount ?? 0,
        vendor: data.extracted?.vendor || '',
        receipt_date: data.extracted?.date || '',
        description: data.extracted?.description || '',
      };

      let status = 'ready';
      // Mark as review if missing project, missing phase (and not new), or invalid amount
      if (!form.project_id || !form.amount || form.amount <= 0 || (form.phase_mode === 'existing' && !form.phase_id)) {
        status = 'review';
      }
      if (data.duplicate) {
        status = 'duplicate';
      }

      setRows((prev) => prev.map((r) => r.id === row.id ? {
        ...r,
        status,
        extracted: data.extracted,
        duplicate: data.duplicate,
        file_id: data.file_id,
        file_hash: data.file_hash,
        form,
      } : r));
    } catch (err) {
      console.error('Bulk extract failed for', row.file.name, err);
      // Stage the file with an error state and a blank form so user can fill manually
      // Note: file_id is null for errors, so onFile will show a friendly message
      setRows((prev) => prev.map((r) => r.id === row.id ? {
        ...r,
        status: 'error',
        errorMessage: err.response?.data?.detail || 'AI extraction failed',
        extracted: null,
        form: {
          project_id: null,
          phase_mode: 'existing',
          phase_id: null,
          new_phase_name: '',
          new_phase_construction_number: 1,
          new_phase_type: 'material',
          cost_type: 'paid',
          amount: 0,
          paid_amount: 0,
          vendor: '',
          receipt_date: '',
          description: '',
        },
      } : r));
    }
  };

  const fileRow = async (row) => {
    if (!row.file_id) {
      // Error rows have no file_id (extraction failed before staging) — fall back to single-file capture path
      try {
        const res = await apiService.quickCaptureExtract(userId, row.file, row.form.project_id);
        const fileId = res.data.file_id;
        const payload = buildFilePayload({ ...row, file_id: fileId });
        await apiService.quickCaptureFile(payload);
        setRows((prev) => prev.map((r) => r.id === row.id ? { ...r, status: 'filed' } : r));
        toast.success('Filed');
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to file');
      }
      return;
    }
    try {
      const payload = buildFilePayload(row);
      await apiService.quickCaptureFile(payload);
      setRows((prev) => prev.map((r) => r.id === row.id ? { ...r, status: 'filed' } : r));
      toast.success('Filed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to file');
    }
  };

  const buildFilePayload = (row) => {
    const f = row.form;
    return {
      user_id: userId,
      file_id: row.file_id,
      file_name: row.file?.name || 'capture',
      project_id: f.project_id,
      phase_mode: f.phase_mode || 'existing',
      phase_id: f.phase_id || null,
      new_phase: f.phase_mode === 'new' ? {
        name: f.new_phase_name,
        construction_phase_number: f.new_phase_construction_number,
        phase_type: f.new_phase_type,
      } : null,
      cost_type: f.cost_type || 'paid',
      amount: parseFloat(f.amount) || 0,
      paid_amount: parseFloat(f.paid_amount || f.amount) || 0,
      vendor: f.vendor,
      receipt_date: f.receipt_date,
      description: f.description,
      document_type: row.extracted?.document_type || 'receipt',
      duplicate_acknowledged: row.status === 'duplicate',
      file_hash: row.file_hash,
    };
  };

  const discardRow = async (row) => {
    if (row.file_id) {
      try { await apiService.quickCaptureDiscard(row.file_id); } catch (e) { /* swallow */ }
    }
    setRows((prev) => prev.map((r) => r.id === row.id ? { ...r, status: 'skipped' } : r));
  };

  // Drop handlers
  const onDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const dt = e.dataTransfer;
    if (dt?.files?.length) await handleFiles(dt.files);
  };
  const onDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const onDragLeave = () => setIsDragging(false);

  // Stats
  const stats = useMemo(() => {
    const c = { ready: 0, review: 0, duplicate: 0, error: 0, filed: 0, skipped: 0, extracting: 0, queued: 0 };
    rows.forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [rows]);

  const allDone = rows.length > 0 && rows.every((r) => r.status === 'filed' || r.status === 'skipped');
  const fileableCount = rows.filter((r) => r.status === 'ready').length;

  const handleFileAllReady = async () => {
    const targets = rows.filter((r) => r.status === 'ready');
    for (const r of targets) {
      // eslint-disable-next-line no-await-in-loop
      await fileRow(r);
    }
    toast.success(`Filed ${targets.length} receipts`);
  };

  const handleClose = () => {
    // Cleanup blob URLs to prevent memory leaks on session
    rows.forEach((r) => {
      try {
        if (r.fullUrl && r.fullUrl.startsWith('blob:')) URL.revokeObjectURL(r.fullUrl);
        if (r.previewUrl && r.previewUrl !== r.fullUrl && r.previewUrl.startsWith('blob:')) URL.revokeObjectURL(r.previewUrl);
      } catch { /* ignore */ }
    });
    if (rows.some((r) => r.status === 'filed') && onComplete) onComplete();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); else onOpenChange(v); }}>
      <DialogContent
        className="max-w-4xl w-[95vw] sm:w-full p-0 gap-0 overflow-hidden"
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: 'min(80vh, 680px)',
          maxHeight: 'min(80vh, 680px)',
        }}
        data-testid="bulk-quick-capture-dialog"
      >
        <DialogHeader className="flex-shrink-0 px-5 py-3 border-b">
          <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Upload className="w-5 h-5 text-amber-600" />
            Bulk Quick Capture
            <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 text-[10px]">NEW</Badge>
          </DialogTitle>
          <DialogDescription className="text-xs sm:text-sm">
            Drop up to {MAX_BATCH} receipts/invoices at once. AI extracts vendor, amount, and date; we auto-flag duplicates and let you file row-by-row.
          </DialogDescription>
        </DialogHeader>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-5 py-3">
        {/* Drop zone */}
        {rows.length === 0 ? (
          <div
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onClick={() => fileInputRef.current?.click()}
            className={`min-h-[200px] sm:min-h-[280px] border-2 border-dashed rounded-2xl flex flex-col items-center justify-center text-center cursor-pointer transition-all p-6 sm:p-10 ${
              isDragging
                ? 'border-amber-500 bg-amber-50'
                : 'border-slate-300 hover:border-amber-400 hover:bg-amber-50/30'
            }`}
            data-testid="bulk-drop-zone"
          >
            <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center mb-3 text-white shadow-xl">
              <Upload className="w-7 h-7 sm:w-8 sm:h-8" />
            </div>
            <p className="font-bold text-slate-900 text-sm sm:text-base">Drop receipts here</p>
            <p className="text-[11px] sm:text-xs text-slate-500 mt-1">or tap to browse — JPG, PNG, HEIC, PDF — up to {MAX_BATCH} files</p>
            <p className="text-[10px] text-slate-400 mt-2">AI will extract vendor, amount &amp; date for each. Duplicates auto-skip.</p>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,application/pdf"
              className="hidden"
              onChange={(e) => handleFiles(e.target.files)}
              data-testid="bulk-file-input"
            />
          </div>
        ) : (
          <>
            {/* Status summary bar */}
            <div className="bg-slate-50 rounded-xl p-3 flex items-center gap-3 flex-wrap text-xs">
              <div className="font-bold text-slate-900 flex items-center gap-1.5">
                <Receipt className="w-3.5 h-3.5" />
                {rows.length} files
              </div>
              {stats.extracting > 0 && (
                <div className="flex items-center gap-1 text-blue-700">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Extracting {stats.extracting}
                </div>
              )}
              {stats.ready > 0 && <span className="text-emerald-700">{stats.ready} ready</span>}
              {stats.review > 0 && <span className="text-amber-700">{stats.review} need review</span>}
              {stats.duplicate > 0 && <span className="text-blue-700">{stats.duplicate} duplicate</span>}
              {stats.error > 0 && <span className="text-red-700">{stats.error} errors</span>}
              {stats.filed > 0 && <span className="text-emerald-800 font-bold">{stats.filed} filed</span>}
              <div className="ml-auto flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="bulk-add-more-btn"
                >
                  + Add more
                </Button>
                {fileableCount > 0 && (
                  <Button
                    size="sm"
                    onClick={handleFileAllReady}
                    className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="bulk-file-all-ready-btn"
                  >
                    File all {fileableCount} ready
                  </Button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*,application/pdf"
                  className="hidden"
                  onChange={(e) => handleFiles(e.target.files)}
                  data-testid="bulk-file-input"
                />
              </div>
            </div>

            {/* Rows */}
            <div className="flex-1 overflow-y-auto space-y-2 pr-1" data-testid="bulk-rows">
              {rows.map((r) => (
                <BulkRow
                  key={r.id}
                  row={r}
                  projects={projects}
                  onUpdate={updateRowForm}
                  onFile={fileRow}
                  onDiscard={discardRow}
                  onPreview={setPreviewRow}
                />
              ))}
            </div>
          </>
        )}
        </div>

        {/* Sticky footer */}
        {rows.length > 0 && (
          <div className="flex-shrink-0 px-5 py-3 border-t bg-white flex items-center justify-between">
            <p className="text-[11px] text-slate-500">
              {allDone ? 'All files processed.' : `${stats.filed + stats.skipped} of ${rows.length} resolved`}
            </p>
            <Button onClick={handleClose} variant={allDone ? 'default' : 'outline'} size="sm" data-testid="bulk-close-btn">
              {allDone ? 'Done' : 'Close'}
            </Button>
          </div>
        )}

        {/* File preview overlay — rendered INSIDE DialogContent (as an absolute layer)
            to inherit the Radix Dialog's pointer-events scope. A sibling Dialog gets
            blocked by Radix's pointer-events:none on body when the parent is open. */}
        {previewRow && (
          <div
            className="absolute inset-0 z-50 flex items-center justify-center p-4 bg-black/80 rounded-lg"
            onClick={() => setPreviewRow(null)}
            data-testid="bulk-preview-modal"
          >
            <div
              className="bg-white rounded-2xl w-full max-w-3xl max-h-[88vh] flex flex-col shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-5 py-3 border-b flex items-start justify-between gap-3 bg-white">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-base font-bold text-slate-900">
                    <Eye className="w-4 h-4 text-amber-600 flex-shrink-0" />
                    <span className="truncate">{previewRow.file?.name || 'Preview'}</span>
                    <StatusPill status={previewRow.status} />
                  </div>
                  <p className="text-xs mt-1 text-slate-600">
                    {previewRow.extracted ? (
                      <>
                        AI extracted: <span className="font-bold">{previewRow.extracted.vendor || 'Unknown'}</span>
                        {' · '}
                        <span className="font-mono font-bold text-slate-900">${(previewRow.extracted.amount || 0).toFixed(2)}</span>
                        {previewRow.extracted.date && <> · {previewRow.extracted.date}</>}
                      </>
                    ) : previewRow.errorMessage ? (
                      <span className="text-red-600">{previewRow.errorMessage}</span>
                    ) : (
                      <span>No data extracted yet</span>
                    )}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setPreviewRow(null)}
                  className="w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center flex-shrink-0"
                  aria-label="Close preview"
                  data-testid="bulk-preview-close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="flex-1 overflow-auto bg-slate-100 flex items-center justify-center min-h-[300px]">
                {previewRow.fullUrl ? (
                  previewRow.isPdf ? (
                    <iframe
                      src={previewRow.fullUrl}
                      title={previewRow.file?.name || 'PDF preview'}
                      className="w-full h-[500px] bg-white"
                      data-testid="bulk-preview-iframe"
                    />
                  ) : (
                    <img
                      src={previewRow.fullUrl}
                      alt={previewRow.file?.name || 'preview'}
                      className="max-w-full max-h-[70vh] object-contain"
                      data-testid="bulk-preview-img"
                    />
                  )
                ) : (
                  <div className="text-slate-400 text-sm py-12">No preview available for this file type.</div>
                )}
              </div>
              <div className="px-5 py-3 border-t flex items-center justify-between bg-white">
                <a
                  href={previewRow.fullUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                >
                  <ExternalLink className="w-3 h-3" />Open in new tab
                </a>
                <Button
                  size="sm"
                  onClick={() => setPreviewRow(null)}
                  className="bg-slate-900 hover:bg-slate-800 text-white"
                  data-testid="bulk-preview-close-btn"
                >
                  Close preview
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BulkQuickCaptureDialog;
